import { Medallion } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import { FEATURE_ICONS } from './feature-families';

import type { FeatureCardData } from './features';
import type { MedallionHue } from '@/components/bedrock';
import type { ReactElement } from 'react';

export interface FeatureRowProps {
  readonly feature: FeatureCardData;
  readonly hue: MedallionHue;
}

/**
 * One capability, as a row: a tinted icon medallion, a title, a sentence.
 *
 * A ROW rather than a card, and the choice is the whole reason the ragged
 * fourteen-in-four-columns hole cannot come back. A grid has to be told how
 * many columns to use and then leaves a gap whenever the item count does not
 * divide; a list has no such arithmetic. Adding a fifteenth capability moves
 * one hairline and nothing else.
 *
 * The medallion is what makes the row scannable. Without it every entry in a
 * family is a bold line over a grey paragraph, and a reader skimming for the
 * one that matters to them has nothing to fix on. It is `aria-hidden` — the
 * icon says nothing the title does not, so announcing it would only make the
 * list longer to hear.
 */
export function FeatureRow({ feature, hue }: FeatureRowProps): ReactElement {
  const { t } = useT();
  const Icon = FEATURE_ICONS[feature.slug];
  return (
    <li
      data-feature={feature.slug}
      className="relative flex gap-4 border-t border-[color:var(--bd-line)] py-6 first:border-t-0 first:pt-0 last:pb-0"
    >
      {Icon ? <Medallion icon={Icon} hue={hue} /> : null}
      <div className="min-w-0">
        <h3 className="text-[1.02rem] font-semibold leading-snug tracking-[-0.02em] text-[color:var(--bd-ink)]">
          {t(feature.titleKey)}
        </h3>
        <p className="mt-1.5 text-pretty text-[0.92rem] leading-[1.62] text-[color:var(--bd-muted)]">
          {/* anchorKey localizes anchors that embed prose (I142); pure markers stay verbatim */}
          {t(feature.descriptionKey, {
            anchor: feature.anchorKey !== undefined ? t(feature.anchorKey) : feature.anchorMetric,
          })}
        </p>
      </div>
    </li>
  );
}
